import {
  BillingCurrency,
  Dependent,
  ExemptionType,
  InvoiceStatus,
  LineItemSupplier,
  PatientType,
  PayerType,
  PaymentMethod,
  PriceableItemOrigin,
  TransactionStatus,
  TransactionType,
} from "elephant-web/gql";
import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_INVOICE_COLLECTION_NAME = "invoice";

export const useRxCollectionInvoice = (): RxCollection<Invoice> | null =>
  useRxCollection<Invoice>(RXDB_INVOICE_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<Invoice>>;
export type InvoiceQueryConstructor = UseRxData["1"];

export const INVOICE_SCHEMA_LITERAL = {
  title: "invoice schema",
  version: 4,
  description: "Patient invoice with embedded line items, payers, transactions",
  type: "object",
  primaryKey: "id",
  properties: {
    id: { type: "string", maxLength: 100 },
    facilityId: { type: "string", maxLength: 100, final: true },
    patientId: { type: "string", maxLength: 100, final: true },
    patientType: { type: "string", enum: Object.values(PatientType) },
    invoiceNumber: { type: "string", final: true },
    invoiceStatus: { type: "string", enum: Object.values(InvoiceStatus) },
    balance: {
      type: "object",
      properties: {
        currency: {
          type: "string",
          enum: Object.values(BillingCurrency),
        },
        total: { type: "number" },
        due: { type: ["number", "null"] },
        paid: { type: ["number", "null"] },
        exempt: { type: ["number", "null"] },
        waived: { type: ["number", "null"] },
      },
      required: ["currency", "total"],
    },
    inpatientDetails: {
      type: ["object", "null"],
      properties: {
        admission: { type: ["string", "null"], format: "date-time" },
        discharge: { type: ["string", "null"], format: "date-time" },
        ward: { type: ["string", "null"] },
        inpatientNumber: { type: ["string", "null"] },
        dailyRebateAmount: { type: ["number", "null"] },
      },
    },
    lineItems: {
      type: "array",
      items: {
        type: "object",
        properties: {
          id: { type: "string", maxLength: 100 },
          cost: { type: "number" },
          quantity: { type: ["integer", "null"] },
          priceListItemId: { type: ["string", "null"] },
          revenueDepartmentId: { type: ["string", "null"] },
          tariffId: { type: ["string", "null"] },
          incurredAt: { type: ["string", "null"], format: "date-time" },
          idempotencyKey: { type: ["string", "null"] },
          automated: { type: ["boolean", "null"] },
          disabled: { type: ["boolean", "null"] },
          missingPrice: { type: ["boolean", "null"] },
          supplier: {
            type: ["string", "null"],
            enum: [...Object.values(LineItemSupplier), null],
          },
          actorId: { type: ["string", "null"] },
          externalReference: {
            type: ["object", "null"],
            properties: {
              origin: {
                type: "string",
                enum: Object.values(PriceableItemOrigin),
              },
              foreignId: { type: "string" },
            },
            required: ["origin", "foreignId"],
          },
        },
        required: ["id", "cost"],
      },
    },
    payers: {
      type: "array",
      items: {
        type: "object",
        properties: {
          id: { type: "string", maxLength: 100 },
          name: { type: "string" },
          type: { type: "string", enum: Object.values(PayerType) },
          balance: {
            type: "object",
            properties: {
              currency: {
                type: "string",
                enum: Object.values(BillingCurrency),
              },
              total: { type: "number" },
              due: { type: ["number", "null"] },
              paid: { type: ["number", "null"] },
              exempt: { type: ["number", "null"] },
              waived: { type: ["number", "null"] },
            },
            required: ["currency", "total"],
          },
          claimedLineItems: {
            type: "array",
            items: {
              type: "object",
              properties: {
                lineItemId: { type: "string", maxLength: 100 },
                amountInPence: { type: "integer" },
              },
              required: ["lineItemId", "amountInPence"],
            },
          },
          insuranceDetails: {
            type: ["object", "null"],
            properties: {
              name: { type: ["string", "null"] },
              insuranceNumber: { type: ["string", "null"] },
              dependent: {
                type: ["string", "null"],
                enum: [...Object.values(Dependent), null],
              },
            },
          },
        },
        required: ["id", "name", "type", "balance", "claimedLineItems"],
      },
    },
    transactions: {
      type: "array",
      items: {
        type: "object",
        properties: {
          id: { type: "string", maxLength: 100 },
          uuid: { type: ["string", "null"] },
          type: { type: "string", enum: Object.values(TransactionType) },
          status: {
            type: "string",
            enum: Object.values(TransactionStatus),
          },
          timestamp: { type: "string", format: "date-time" },
          actionedByUserId: { type: ["string", "null"] },
          amount: {
            type: ["object", "null"],
            properties: {
              currency: {
                type: "string",
                enum: Object.values(BillingCurrency),
              },
              value: { type: "number" },
            },
            required: ["currency", "value"],
          },
          paymentMethod: {
            type: ["string", "null"],
            enum: [...Object.values(PaymentMethod), null],
          },
          paymentAuthCode: { type: ["string", "null"] },
          exemptionType: {
            type: ["string", "null"],
            enum: [...Object.values(ExemptionType), null],
          },
          notes: { type: ["string", "null"] },
          payerName: { type: ["string", "null"] },
          payerType: {
            type: ["string", "null"],
            enum: [...Object.values(PayerType), null],
          },
          refundReason: { type: ["string", "null"] },
          refundPaymentMethod: {
            type: ["string", "null"],
            enum: [...Object.values(PaymentMethod), null],
          },
          refundAuthCode: { type: ["string", "null"] },
        },
        required: ["id", "type", "status", "timestamp"],
      },
    },
    meta: SYNCABLE_META,
    finalizedAt: { type: ["string", "null"], format: "date-time" },
    finalizedByUserId: { type: ["string", "null"] },
    priceListLastSyncedAt: {
      type: ["string", "null"],
      format: "date-time",
    },
  },
  required: [
    "id",
    "facilityId",
    "patientId",
    "patientType",
    "invoiceNumber",
    "invoiceStatus",
    "balance",
    "inpatientDetails",
    "lineItems",
    "payers",
    "transactions",
    "meta",
    "finalizedAt",
    "finalizedByUserId",
    "priceListLastSyncedAt",
  ],
  indexes: ["patientId", "facilityId"],
  encrypted: [
    "patientType",
    "invoiceNumber",
    "invoiceStatus",
    "balance",
    "inpatientDetails",
    "lineItems",
    "payers",
    "transactions",
    "meta",
    "finalizedAt",
    "finalizedByUserId",
    "priceListLastSyncedAt",
  ],
} as const;

const schemaTyped = toTypedRxJsonSchema(INVOICE_SCHEMA_LITERAL);

export type Invoice = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export type InvoicePayer = Invoice["payers"][number];

export const INVOICE_SCHEMA: RxJsonSchema<Invoice> = INVOICE_SCHEMA_LITERAL;
